import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Plus,
  Trash2,
  KeyRound,
  Eye,
  EyeOff,
  Download,
  ShieldCheck,
  UserPlus,
  RefreshCw,
  Briefcase,
  Check,
  X,
} from "lucide-react";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import Layout from "@/components/Layout";
import ReportExportMenu from "@/components/ReportExportMenu";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail, API } from "@/lib/api";

const PERMISSION_DEFS = [
  { key: "view_all", label: "View all shipments", desc: "If off, the user only sees their own shipments." },
  { key: "can_edit", label: "Edit shipments", desc: "Allow editing shipment details." },
  { key: "can_delete", label: "Delete shipments", desc: "Allow deleting saved shipments." },
  { key: "can_change_status", label: "Change shipment status", desc: "Allow status updates (Received, In transit, etc.)" },
  { key: "can_change_payment", label: "Update customer payment status", desc: "Restricted — usually one accounts user. Others can view but not change." },
  { key: "view_financials", label: "View full financials", desc: "Allow switching Sales/Cost/Margin in the shipments list (finance role). Operators without this only see Margin." },
  { key: "can_export", label: "Export CSV report", desc: "Allow this user to download / export the shipments CSV report (period filters + custom range)." },
];

export default function Admin() {
  const { user, logout } = useAuth();
  const [users, setUsers] = useState([]);
  const [requests, setRequests] = useState([]);
  const [salesPersons, setSalesPersons] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [u, r, sp] = await Promise.all([
        api.get("/admin/users"),
        api.get("/admin/password-requests"),
        api.get("/admin/sales-persons"),
      ]);
      setUsers(u.data || []);
      setRequests(r.data || []);
      setSalesPersons(sp.data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-zinc-900 flex items-center gap-3">
              <ShieldCheck className="w-7 h-7 text-blue-700" />
              Admin Console
            </h1>
            <p className="text-sm text-zinc-500 mt-1">
              Manage users, permissions, password resets and reports.
            </p>
          </div>
          <div className="flex gap-2">
            <Button onClick={loadAll} variant="outline" data-testid="admin-refresh">
              <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
            </Button>
            <ReportExportMenu testid="admin-download-report" />
          </div>
        </div>

        <Tabs defaultValue="users">
          <TabsList>
            <TabsTrigger value="users" data-testid="tab-users">Users</TabsTrigger>
            <TabsTrigger value="sales-persons" data-testid="tab-sales-persons">
              Sales Persons {salesPersons.length > 0 && (
                <Badge variant="outline" className="ml-2">{salesPersons.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="requests" data-testid="tab-requests">
              Password Requests {requests.length > 0 && (
                <Badge variant="destructive" className="ml-2">{requests.length}</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="users" className="mt-4">
            <UsersPanel users={users} setUsers={setUsers} reload={loadAll} currentUser={user} />
          </TabsContent>

          <TabsContent value="sales-persons" className="mt-4">
            <SalesPersonsPanel
              items={salesPersons}
              setItems={setSalesPersons}
            />
          </TabsContent>

          <TabsContent value="requests" className="mt-4">
            <RequestsPanel
              requests={requests}
              users={users}
              reload={loadAll}
            />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}

function UsersPanel({ users, setUsers, reload, currentUser }) {
  const [createOpen, setCreateOpen] = useState(false);
  const [resetUser, setResetUser] = useState(null);

  const handleDelete = async (u) => {
    if (u.id === currentUser.id) {
      toast.error("Cannot delete yourself");
      return;
    }
    if (!confirm(`Delete user ${u.email}?`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      setUsers((arr) => arr.filter((x) => x.id !== u.id));
      toast.success("User deleted");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    }
  };

  const togglePerm = async (u, key) => {
    const newPerms = { ...(u.permissions || {}), [key]: !u.permissions?.[key] };
    try {
      await api.put(`/admin/users/${u.id}`, { permissions: newPerms });
      setUsers((arr) => arr.map((x) => x.id === u.id ? { ...x, permissions: newPerms } : x));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed");
    }
  };

  const changeRole = async (u, role) => {
    try {
      await api.put(`/admin/users/${u.id}`, { role });
      setUsers((arr) => arr.map((x) => x.id === u.id ? { ...x, role } : x));
      toast.success("Role updated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed");
    }
  };

  return (
    <>
      <div className="flex justify-end mb-3">
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button className="bg-zinc-900 hover:bg-zinc-800 text-white" data-testid="btn-create-user">
              <UserPlus className="w-4 h-4 mr-1.5" /> Add User
            </Button>
          </DialogTrigger>
          <DialogContent>
            <CreateUserForm onCreated={() => { setCreateOpen(false); reload(); }} />
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border-zinc-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr className="text-left text-[10px] uppercase tracking-[0.14em] text-zinc-500">
                <th className="px-4 py-3">User</th>
                <th className="px-4 py-3">Role</th>
                <th className="px-4 py-3">Permissions</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {users.length === 0 ? (
                <tr><td colSpan="4" className="px-4 py-10 text-center text-zinc-500">No users yet.</td></tr>
              ) : users.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-zinc-900">{u.email}</div>
                    <div className="text-[11px] text-zinc-500">{u.name || "—"}</div>
                  </td>
                  <td className="px-4 py-3">
                    <Select value={u.role} onValueChange={(v) => changeRole(u, v)} disabled={u.id === currentUser.id}>
                      <SelectTrigger className="h-8 w-32 text-xs" data-testid={`role-${u.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">admin</SelectItem>
                        <SelectItem value="user">user</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-4 py-3">
                    {u.role === "admin" ? (
                      <Badge variant="outline" className="text-xs">All permissions</Badge>
                    ) : (
                      <div className="flex flex-wrap gap-3">
                        {PERMISSION_DEFS.map((p) => (
                          <label key={p.key} className="flex items-center gap-1.5 text-xs text-zinc-700">
                            <Checkbox
                              checked={!!u.permissions?.[p.key]}
                              onCheckedChange={() => togglePerm(u, p.key)}
                              data-testid={`perm-${p.key}-${u.id}`}
                            />
                            <span>{p.label}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setResetUser(u)}
                        data-testid={`btn-reset-pw-${u.id}`}
                      >
                        <KeyRound className="w-4 h-4 mr-1" /> Reset Password
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(u)}
                        disabled={u.id === currentUser.id}
                        data-testid={`btn-delete-user-${u.id}`}
                        className="hover:text-red-600 hover:border-red-300"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <ResetPasswordDialog
        target={resetUser}
        onClose={() => setResetUser(null)}
      />
    </>
  );
}

function CreateUserForm({ onCreated }) {
  const [form, setForm] = useState({
    email: "",
    name: "",
    password: "",
    role: "user",
    permissions: {
      view_all: false,
      can_edit: false,
      can_delete: false,
      can_change_status: false,
      can_change_payment: false,
      view_financials: false,
      can_export: false,
    },
  });
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const togglePerm = (key) =>
    setForm((f) => ({ ...f, permissions: { ...f.permissions, [key]: !f.permissions[key] } }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/admin/users", form);
      toast.success("User created");
      onCreated();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Create failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>Add New User</DialogTitle>
        <DialogDescription>
          Create a user account, set their role, and assign per-user permissions.
        </DialogDescription>
      </DialogHeader>
      <form onSubmit={submit} className="space-y-4 mt-2">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-500">Email</Label>
            <Input
              required
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="h-11 mt-1"
              data-testid="new-user-email"
            />
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-500">Name</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="h-11 mt-1"
              data-testid="new-user-name"
            />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-500">Password</Label>
            <div className="relative">
              <Input
                required
                type={show ? "text" : "password"}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="h-11 mt-1 pr-10"
                data-testid="new-user-password"
              />
              <button
                type="button"
                onClick={() => setShow((s) => !s)}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-zinc-500 hover:text-zinc-900"
                tabIndex={-1}
                data-testid="new-user-toggle-password"
              >
                {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-500">Role</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger className="h-11 mt-1" data-testid="new-user-role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">admin</SelectItem>
                <SelectItem value="user">user</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {form.role === "user" && (
          <div className="border-t pt-4">
            <div className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-500 mb-2">
              Permissions
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {PERMISSION_DEFS.map((p) => (
                <label key={p.key} className="flex items-start gap-2 p-2 border border-zinc-200 rounded hover:bg-zinc-50">
                  <Checkbox
                    checked={form.permissions[p.key]}
                    onCheckedChange={() => togglePerm(p.key)}
                    data-testid={`new-perm-${p.key}`}
                  />
                  <div>
                    <div className="text-sm font-medium text-zinc-800">{p.label}</div>
                    <div className="text-[11px] text-zinc-500">{p.desc}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button
            type="submit"
            disabled={submitting}
            className="bg-blue-700 hover:bg-blue-800 text-white"
            data-testid="new-user-submit"
          >
            <Plus className="w-4 h-4 mr-1.5" /> {submitting ? "Creating…" : "Create User"}
          </Button>
        </DialogFooter>
      </form>
    </>
  );
}

function ResetPasswordDialog({ target, onClose, requestId = null }) {
  const [pw, setPw] = useState("");
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { setPw(""); setShow(false); }, [target]);

  if (!target) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/admin/reset-password", {
        user_id: target.user_id || target.id,
        new_password: pw,
        request_id: requestId || undefined,
      });
      toast.success("Password reset", { description: target.email });
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Reset failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reset password — {target.email}</DialogTitle>
          <DialogDescription>
            Set a new password (min 6 characters) for this user. They will be notified by you out-of-band.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3 mt-2">
          <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-500">New password</Label>
          <div className="relative">
            <Input
              required
              type={show ? "text" : "password"}
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              minLength={6}
              className="h-11 pr-10"
              data-testid="reset-pw-input"
            />
            <button
              type="button"
              onClick={() => setShow((s) => !s)}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-zinc-500 hover:text-zinc-900"
              tabIndex={-1}
              data-testid="reset-pw-toggle"
            >
              {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={submitting} className="bg-blue-700 hover:bg-blue-800 text-white" data-testid="reset-pw-submit">
              {submitting ? "Resetting…" : "Reset Password"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function SalesPersonsPanel({ items, setItems }) {
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");

  const add = async (e) => {
    e.preventDefault();
    const n = name.trim();
    if (!n) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/admin/sales-persons", { name: n });
      setItems((arr) => [...arr, data].sort((a, b) => a.name.localeCompare(b.name)));
      setName("");
      toast.success("Sales person added");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  const saveEdit = async (sp) => {
    const n = editName.trim();
    if (!n) return;
    try {
      await api.put(`/admin/sales-persons/${sp.id}`, { name: n });
      setItems((arr) => arr.map((x) => x.id === sp.id ? { ...x, name: n } : x).sort((a, b) => a.name.localeCompare(b.name)));
      setEditingId(null);
      toast.success("Updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    }
  };

  const remove = async (sp) => {
    if (!confirm(`Delete sales person "${sp.name}"?`)) return;
    try {
      await api.delete(`/admin/sales-persons/${sp.id}`);
      setItems((arr) => arr.filter((x) => x.id !== sp.id));
      toast.success("Removed");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    }
  };

  return (
    <Card className="border-zinc-200 shadow-sm">
      <div className="p-5">
        <form onSubmit={add} className="flex gap-2 mb-4">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Add sales person (e.g. Aman Kumar)"
            className="h-10"
            data-testid="sp-input-name"
          />
          <Button
            type="submit"
            disabled={submitting || !name.trim()}
            className="bg-zinc-900 hover:bg-zinc-800 text-white"
            data-testid="sp-add-btn"
          >
            <Plus className="w-4 h-4 mr-1" /> Add
          </Button>
        </form>

        {items.length === 0 ? (
          <div className="text-sm text-zinc-500 py-8 text-center" data-testid="sp-empty">
            <Briefcase className="w-8 h-8 mx-auto mb-2 text-zinc-300" />
            No sales persons yet. Add one above to enable the dropdown in the Create form.
          </div>
        ) : (
          <ul className="divide-y divide-zinc-100">
            {items.map((sp) => (
              <li
                key={sp.id}
                className="flex items-center justify-between py-2.5 px-1 group"
                data-testid={`sp-row-${sp.id}`}
              >
                {editingId === sp.id ? (
                  <div className="flex gap-2 flex-1">
                    <Input
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      className="h-9"
                      data-testid={`sp-edit-input-${sp.id}`}
                      autoFocus
                    />
                    <Button
                      size="sm"
                      onClick={() => saveEdit(sp)}
                      data-testid={`sp-save-${sp.id}`}
                      className="h-9 bg-blue-700 hover:bg-blue-800 text-white"
                    >
                      <Check className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setEditingId(null)}
                      className="h-9"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-zinc-100 flex items-center justify-center text-zinc-600 text-xs font-semibold">
                        {sp.name.split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase()}
                      </div>
                      <div className="text-sm font-medium text-zinc-900">{sp.name}</div>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => { setEditingId(sp.id); setEditName(sp.name); }}
                        data-testid={`sp-edit-${sp.id}`}
                      >
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => remove(sp)}
                        data-testid={`sp-delete-${sp.id}`}
                        className="text-zinc-500 hover:text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </Card>
  );
}

function RequestsPanel({ requests, users, reload }) {
  const [resetTarget, setResetTarget] = useState(null);

  const dismiss = async (r) => {
    try {
      await api.delete(`/admin/password-requests/${r.id}`);
      toast.success("Dismissed");
      reload();
    } catch (e) {
      toast.error("Failed to dismiss");
    }
  };

  return (
    <Card className="border-zinc-200 shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr className="text-left text-[10px] uppercase tracking-[0.14em] text-zinc-500">
              <th className="px-4 py-3">User Email</th>
              <th className="px-4 py-3">Requested At</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {requests.length === 0 ? (
              <tr><td colSpan="3" className="px-4 py-10 text-center text-zinc-500" data-testid="no-requests">
                No pending password reset requests.
              </td></tr>
            ) : requests.map((r) => (
              <tr key={r.id} data-testid={`request-row-${r.id}`}>
                <td className="px-4 py-3 font-medium text-zinc-900">{r.email}</td>
                <td className="px-4 py-3 text-xs font-mono text-zinc-500">
                  {new Date(r.requested_at).toLocaleString()}
                </td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-2">
                    <Button
                      size="sm"
                      onClick={() => setResetTarget(r)}
                      data-testid={`btn-handle-${r.id}`}
                      className="bg-blue-700 hover:bg-blue-800 text-white"
                    >
                      <KeyRound className="w-4 h-4 mr-1" /> Reset
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => dismiss(r)}
                      data-testid={`btn-dismiss-${r.id}`}
                    >
                      Dismiss
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ResetPasswordDialog
        target={resetTarget}
        requestId={resetTarget?.id}
        onClose={() => { setResetTarget(null); reload(); }}
      />
    </Card>
  );
}


